import React, { useState } from "react";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageContainer from "../components/pages/PageContainer";
import GameSearchBar from "../components/Sidebar/GameSearchBar";
import Menu from "./Game/Menu";
import { Outlet, useOutletContext, useLocation } from "react-router";
import NewTraderGuide from "../components/guide/NewTraderGuide";
import { useNewTraderGuide } from "../context/NewTraderGuideContext";
import { isLeagueFocusedPath } from "../utils/leagueCreationRoutes";
import useWindowWidth from "../hooks/useWindowWidth";

const Contest = () => {
  const [sidebarToggle] = useOutletContext();
  const [search, setSearch] = useState("");
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  // Live width, unlike `isMobileView` above, which is seeded once and never
  // updated. The focused-flow gate below has to survive a resize.
  const isMobile = useWindowWidth() < 768;
  const location = useLocation();
  const { guideActive, guideStep } = useNewTraderGuide();
  const persona = localStorage.getItem("qushi_onboarding_persona");

  const handleChange = (e) => {
    setSearch(e.target.value);
  };

  const isLandingPage = location.pathname === "/contest";

  // The grey desktop heading band this string fed is gone (see below); every
  // League Play screen names itself now.

  // Check if the current path matches "/contest/game-detail/[number]"
  // const isGameDetailPage = /^\/contest\/game-detail\/\d+$/.test(location.pathname);

  const isGameDetailPage = /^\/contest\/(game-detail\/\d+|[^/]+(?<!my-games|add-league|cash-leagues|public-leagues|create-public-league|create-private-league|private-league-created|public-league-created|private-league-preview|private-league-joined))$/.test(location.pathname);

  // The invite confirmation page is reached as /contest/join/<slug>, so it needs
  // a prefix test rather than an exact path like the rest of the flow.
  const isInviteLandingPage = location.pathname.startsWith("/contest/join/");
  // A league's own screens under /contest carry their own title (the league shell,
  // or a page heading of their own), so the generic heading band and league menu
  // would double up.
  // `notifications` and `chat` join the list for the same reason: they are the
  // main app's own screens, each printing its own title, opened inside the league
  // shell (client Rev, desktop). The grey "Leagues" band above would be a second
  // heading on top of theirs.
  const isLeagueSubPage =
    /^\/contest\/[^/]+\/(portfolio|details|settings|notifications|chat)$/.test(
      location.pathname
    );

  // Screens that print their own title inside the content area, so the grey
  // heading band above (and the search + tab row that comes with it) would repeat
  // it. `isWizardPage` is the flag that suppresses all three; the name predates
  // these pages being anything other than the create flow.
  const isSelfTitledPage =
    location.pathname === "/contest/public-leagues" ||
    location.pathname === "/contest/my-games" ||
    // Cash Games "View All". Same shape as Public Leagues — its own "Cash Games"
    // title, its own subtitle, then the metabar and the three phase tabs — so
    // the generic heading band, the search bar and the legacy pill Menu would
    // all be a second, competing header above it. Before this it was the legacy
    // card grid, which needed that chrome.
    location.pathname === "/contest/cash-leagues";

  // The Create League screens that carry their own focused header. Client Rev 1:
  // "Remove the QUSHi logo, the three-dot menu, the League Detail label, and the
  // bottom app navigation during creation." So DashboardHeader is not rendered
  // at all here, and the top offset that clears it goes with it — the screen's
  // own fixed header owns the top of the window instead.
  //
  // MOBILE ONLY, since client Rev 3: "Normal QUSHi desktop shell - centered
  // QUSHi logo, avatar, notification, chats. Our desktop header was removed and
  // replaced with the mobile league creation header." Rev 1's instruction was
  // written about the mobile review — "I am sorry, but the entire time i kept
  // saying I am reviewing mobile" — so on desktop these screens keep the ordinary
  // app chrome and its top offset, and LeagueWizard (which reads the same 768px
  // breakpoint) renders its left-aligned title instead of the focused header.
  const isFocusedCreationFlow = isLeagueFocusedPath(location.pathname) && isMobile;

  const isWizardPage =
    isInviteLandingPage ||
    isLeagueSubPage ||
    isSelfTitledPage ||
    location.pathname === "/contest/create-public-league" ||
    location.pathname === "/contest/create-private-league" ||
    location.pathname === "/contest/private-league-created" ||
    location.pathname === "/contest/public-league-created" ||
    location.pathname === "/contest/private-league-preview" ||
    location.pathname === "/contest/private-league-joined";



  return (
    <>
      {!isFocusedCreationFlow && <DashboardHeader toggle={sidebarToggle} />}

      {/* This block is also what clears the fixed header — every page under
          /contest needs its top offset, including the ones with no heading band,
          or their first line renders underneath the header.
          A league's own screens are the exception: LeagueShell carries its own
          offset (as the league Home already does), so adding one here too would
          push them down twice. */}
      {!isGameDetailPage && !isLeagueSubPage && !isFocusedCreationFlow && (
        <div
          className={`md:ml-60 bg-white ${
            // League Play (/contest) hides the "Leagues" heading. Use white
            // top padding (not a transparent margin) so the grey adminLayout
            // background can't peek out as a strip below the fixed header.
            location.pathname === "/contest"
              ? // Matches the Leaderboard's own top offset exactly (pt-20 md:pt-24
                // in LeaderboardSecton), so League Play's title sits the same
                // distance below the fixed header as the Leaderboard's does. The
                // mobile header is shorter than the desktop one, hence two values.
                "pt-20 md:pt-24"
              : isWizardPage
              ? // The create/invite screens have no heading band to fill the
                // offset, so it's pure empty space — clear the fixed header and
                // nothing more. The mobile header is roughly half the height of
                // the desktop one, hence the two values.
                //
                // PADDING, not margin — same reason as the /contest branch above.
                // A margin here is transparent, so the slate-100 `.adminLayout`
                // background showed through the part of the offset the fixed
                // header does not cover: a grey strip under the header on
                // Public Leagues and My Leagues. That strip is the last piece of
                // client Rev 4's "Remove the old gray background strip behind
                // the Public Leagues content area" — the heading band inside
                // this div was removed then, but the div's own transparent
                // offset was still painting grey. Same total offset either way.
                "pt-14 md:pt-24"
              : "mt-24"
          }`}
        >
          {/* The grey desktop heading band used to live here. Client Rev 4:
              "Remove the old gray background strip behind the Public Leagues
              content area." It was the last survivor of the old header system the
              client had already asked us to drop in Rev 3 ("remove ALL text
              headers under the QUSHi logo... replaced with the new header
              system") — every League Play screen now carries its own title in
              its own content column, so the band was an empty grey strip above a
              page that had already introduced itself.
              The wrapper stays: it is what clears the fixed header. */}
        </div>
      )}

      <PageContainer>
        {/* Conditionally render GameSearchBar */}
        {!isGameDetailPage && !isLandingPage && !isWizardPage && location.pathname !== "/contest/add-league" && !/\/[^/]+\/edit$/.test(location.pathname) &&(
          <GameSearchBar
            search={search}
            handleChange={handleChange}
            handleSubmit={(e) => e.preventDefault()} // Prevent form submission
            isMobileView={isMobileView}
            clearSearch={() => setSearch("")}
          />
        )}

        {/* Conditionally render Menu */}
        {!isGameDetailPage && !isLandingPage && !isWizardPage && location.pathname !== "/contest/add-league" && !/\/[^/]+\/edit$/.test(location.pathname) && <Menu />}

        {/* Pass search and handleChange to child components via Outlet context */}
        <Outlet context={{ search, handleChange }} />
      </PageContainer>
      {guideActive && guideStep === 4 && persona === "experienced" && (
        <NewTraderGuide step={4} />
      )}
    </>
  );
};

export default Contest;
